import type { ReactElement } from 'react';
import { ClockIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { Button } from '@csa/ui';

/**
 * Shown to an authenticated user who holds no usable entitlements: either a
 * newly provisioned account awaiting a role, or one an administrator has
 * disabled.
 *
 * Both states are deliberate, so both get a clear explanation rather than an
 * empty dashboard. Provisioning on first sign-in with zero permissions is what
 * lets an access review list everyone who has ever reached the tool, instead
 * of only the people someone remembered to add.
 */
export function AccessPending({
  reason,
  onSignOut,
  variant,
}: {
  reason: string;
  onSignOut: () => void;
  variant: 'pending' | 'disabled';
}): ReactElement {
  const disabled = variant === 'disabled';
  const Icon = disabled ? NoSymbolIcon : ClockIcon;

  return (
    <div className="flex min-h-screen items-center justify-center bg-sunken p-6">
      <div className="w-full max-w-lg rounded-lg border border-line bg-surface p-8 shadow-card">
        <div className="flex items-start gap-4">
          <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-subtle">
            <Icon className="size-5 text-brand" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold tracking-tight text-body">
              {disabled ? 'Your access has been disabled' : 'No access assigned yet'}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              {disabled
                ? 'An administrator has disabled your access to this console.'
                : 'Your account exists but no role has been assigned, so there is nothing you can do here yet. Ask your manager to raise an access request.'}
            </p>

            <p className="mt-4 rounded-md border border-line bg-sunken px-3 py-2.5 text-sm text-body">
              {reason}
            </p>

            <div className="mt-6">
              <Button onClick={onSignOut}>Sign out</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
